import React, { useState } from 'react';
import styled from 'styled-components';
import { object, string, array, boolean } from 'yup';
import { Box, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { PAGE_TEXT } from '../../lang';
import {
  type DeclarationValues,
  NominationDeclaration,
  useConfirmCoSupDec,
} from '../../../graphql/NominationForm';
import {
  Accordion,
  Alert,
  type AlertDetails,
  Button,
  Checkbox,
} from '../../../../../components';

type CoSupervisorDeclarationProps = {
  coSupEmail: string;
  coSupervisorDeclarationRemark: string;
  nominationId: string;
  submitted: boolean;
  refetchCoSupDec: () => void;
};

const pageText = PAGE_TEXT.DECLARATION;

export const CoSupervisorDeclaration: React.FC<
  CoSupervisorDeclarationProps
> = props => {
  const {
    submitted,
    refetchCoSupDec,
    nominationId,
    coSupEmail,
    coSupervisorDeclarationRemark,
  } = props;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const DeclarationLabels = [
    pageText.FORALL.DECLARATION_1,
    pageText.FORALL.DECLARATION_2,
  ];

  const initialValues: DeclarationValues[] = [
    {
      content: NominationDeclaration.NO_INTEREST_CONFLICT,
      // if have not submitted, each `confirmed` should be initially be false
      // if have submitted, then the first `confirmed` would depend on the value of coSupervisorDeclarationRemark
      confirmed: submitted ? coSupervisorDeclarationRemark === '' : false,
      justification: coSupervisorDeclarationRemark,
    },
    {
      content: NominationDeclaration.NOT_REGISTER_HIGHER_DEGREE,
      confirmed: submitted,
      justification: '',
    },
  ];

  const confirmCoSupDec = useConfirmCoSupDec();
  const handleSubmit = async (value: DeclarationValues[]): Promise<void> => {
    const { confirmed, justification } = value?.[0];
    const variables = {
      nominationId,
      emailAddress: coSupEmail,
      confirmed: true,
      ...(confirmed ? null : { coSupervisorDeclarationRemark: justification }),
    };
    try {
      await confirmCoSupDec({
        variables,
      });
      setAlertDetails({
        alertContent: 'Submitted Declaration Successfully.',
        alertType: 'success',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      setAlertDetails({
        alertContent:
          'Submitted Declaration Failed.' + (error as Error)?.message,
        alertType: 'error',
      });
    }
    refetchCoSupDec();
    setOpenAlert(true);
  };

  const formik = useFormik<DeclarationValues[]>({
    initialValues,
    validationSchema: array().of(
      object().shape({
        content: string().required('Required'),
        confirmed: boolean().when(['content'], {
          is: (content: string) => {
            return content !== NominationDeclaration.NO_INTEREST_CONFLICT;
          },
          then: schema => schema.required().oneOf([true]),
          otherwise: schema => schema.notRequired(),
        }),
        justification: string().when(['content', 'confirmed'], {
          is: (content: string, confirmed: boolean) => {
            return (
              content === NominationDeclaration.NO_INTEREST_CONFLICT &&
              !confirmed
            );
          },

          then: schema => schema.required('Required'),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    ),
    onSubmit: handleSubmit,
  });

  const touched = formik.touched;
  const errors = formik.errors;

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Accordion
        style={{ width: '100%' }}
        title={pageText.CO_SUPERVISOR_TITLE}
        defaultExpanded
        details={
          <form onSubmit={formik.handleSubmit} noValidate>
            <Stack className="declaration-box">
              <Box padding={2}>
                {initialValues.map((label, index) => {
                  const contentLabel = DeclarationLabels[index];

                  return (
                    <div key={index}>
                      <Checkbox
                        disabled={submitted}
                        name={`[${index}].confirmed`}
                        label={
                          <div
                            className="mt-2"
                            dangerouslySetInnerHTML={{ __html: contentLabel }}
                          />
                        }
                        sx={{
                          alignItems: 'flex-start',
                        }}
                        value={formik.values[index]?.confirmed}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          Boolean(touched[index]?.confirmed) &&
                          Boolean(errors[index]?.confirmed)
                        }
                      />
                      {!formik.values[0].confirmed && index === 0 && (
                        <Box className="text-Box">
                          <h3 className="font-bold mb-4">
                            {pageText.FORALL.DECLARATION_1_SUB}
                          </h3>
                          <TextField
                            disabled={submitted}
                            name={`[0].justification`}
                            className="w-full"
                            onChange={formik.handleChange}
                            InputLabelProps={{ shrink: true }}
                            onBlur={formik.handleBlur}
                            value={
                              formik.values.length > 0
                                ? formik.values[0].justification
                                : ''
                            }
                            error={
                              !!(
                                touched !== undefined &&
                                touched[0]?.justification &&
                                errors?.[0]?.justification !== undefined &&
                                errors[0]?.justification !== ''
                              )
                            }
                            helperText={
                              touched !== undefined &&
                              touched[0]?.justification &&
                              errors?.[0]?.justification !== undefined &&
                              errors[0]?.justification
                            }
                            minRows={3}
                            multiline
                            required
                          />
                        </Box>
                      )}
                    </div>
                  );
                })}
              </Box>
            </Stack>
            <div className="flex flex-row justify-center align-center w-100 my-[1rem]">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={submitted}
              >
                {submitted ? PAGE_TEXT.SUBMITTED : PAGE_TEXT.SUBMIT}
              </Button>
            </div>
          </form>
        }
      />
    </Container>
  );
};

const Container = styled.div`
  .text-Box {
    margin: 0.75rem;
    margin-left: 2.25rem;
    margin-right: 2.25rem;
  }
`;
